import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Analytics } from '@vercel/analytics/react';
import Landing from './pages/Landing';
import { ADMIN_SESSION_INVALID_EVENT } from './services/adminApi';
import { scrubAnalyticsEvent } from './analyticsScrub';

// Landing is the entry point and stays in the initial chunk. Everything else
// is split out of it — the marketing route must not pay for leaflet,
// framer-motion, exifr or the admin console. Lighthouse mobile ≥ 90 is a
// hard gate and the landing JS budget is 170 kB gzipped.
const DiscoverMore = lazy(() => import('./pages/DiscoverMore'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Verify = lazy(() => import('./pages/Verify'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Results = lazy(() => import('./pages/Results'));
const Settings = lazy(() => import('./pages/Settings'));
const InteractiveMapPage = lazy(() => import('./pages/InteractiveMapPage'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const Help = lazy(() => import('./pages/Help'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const FAQ = lazy(() => import('./pages/FAQ'));
const SocialContent = lazy(() => import('./pages/SocialContent'));
const Invite = lazy(() => import('./pages/invite/Invite'));

const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminGrowth = lazy(() => import('./pages/admin/AdminGrowth'));
const AdminRevenue = lazy(() => import('./pages/admin/AdminRevenue'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminEmails = lazy(() => import('./pages/admin/AdminEmails'));

const RouteFallback = () => (
  <div className="min-h-screen bg-void flex items-center justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-transparent border-b-gold-400" />
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <RouteFallback />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');

  if (!adminToken) {
    return <Navigate to="/admin" />;
  }

  // Validate JWT is not expired
  try {
    const payload = JSON.parse(atob(adminToken.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      return <Navigate to="/admin" />;
    }
  } catch {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    return <Navigate to="/admin" />;
  }

  return children;
};

const AdminSessionRedirector = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const redirectToLogin = () => navigate('/admin', { replace: true });
    window.addEventListener(ADMIN_SESSION_INVALID_EVENT, redirectToLogin);
    return () => window.removeEventListener(ADMIN_SESSION_INVALID_EVENT, redirectToLogin);
  }, [navigate]);

  return null;
};

// Public Route wrapper (redirect to dashboard if authenticated, except for landing)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <RouteFallback />;
  }

  // Redirect authenticated users to dashboard (except landing page handles this itself)
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AdminSessionRedirector />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Landing page - public */}
              <Route path="/" element={<Landing />} />
              <Route path="/discover" element={<DiscoverMore />} />

              {/* Legal pages - public */}
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/help" element={<Help />} />

              {/* Blog pages - public */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/social" element={<AdminProtectedRoute><SocialContent /></AdminProtectedRoute>} />

              {/* Invite landing - public; the token never reaches analytics */}
              <Route path="/i/:token" caseSensitive element={<Invite />} />

              {/* Public routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />

              <Route path="/register" element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } />

              <Route path="/verify" element={
                <PublicRoute>
                  <Verify />
                </PublicRoute>
              } />

              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />

              <Route path="/results" element={
                <ProtectedRoute>
                  <Results />
                </ProtectedRoute>
              } />

              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />

              <Route path="/map" element={
                <ProtectedRoute>
                  <InteractiveMapPage />
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
              <Route path="/admin/growth" element={<AdminProtectedRoute><AdminGrowth /></AdminProtectedRoute>} />
              <Route path="/admin/revenue" element={<AdminProtectedRoute><AdminRevenue /></AdminProtectedRoute>} />
              <Route path="/admin/analytics" element={<AdminProtectedRoute><AdminAnalytics /></AdminProtectedRoute>} />
              <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
              <Route path="/admin/emails" element={<AdminProtectedRoute><AdminEmails /></AdminProtectedRoute>} />

              {/* 404 */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          <Analytics beforeSend={scrubAnalyticsEvent} />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
